import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import CategoryDropdown from "./components/CategoryDropdown";
import CategoryGrid from "./components/CategoryGrid";
import ProductCard from "@/components/feature/ProductCard";
import { featuredProducts } from "@/mocks/products";
import { MAIN_SITE_URL } from "@/data/categories";

export default function Home() {
  const { t } = useTranslation();

  return (
    <>
      <Header />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Soft%20pastel%20minimal%20flat%20lay%20of%20sneakers%20hoodies%20caps%20and%20shopping%20bags%20arranged%20on%20a%20clean%20light%20sage%20green%20background%2C%20airy%20European%20lifestyle%20aesthetic%2C%20soft%20natural%20daylight%2C%20editorial%20product%20photography%2C%20subtle%20shadows%2C%20harmonious%20composition&width=1600&height=700&seq=allchina-hero-2026&orientation=landscape"
              alt="AllChinaBuy spreadsheet product categories"
              title="AllChinaBuy spreadsheet products"
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/55 via-foreground-950/35 to-background-50" />
          </div>

          <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-20 text-center md:px-6 md:py-32">
            <span className="inline-flex items-center gap-2 rounded-full bg-background-50/90 px-4 py-1.5 text-xs font-medium text-foreground-700">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-refresh-line text-xs"></i>
              </span>
              {t("hero.badge")}
            </span>

            <h1 className="mt-5 max-w-3xl text-3xl font-bold leading-tight text-background-50 md:text-5xl">
              {t("hero.title")}
            </h1>

            <p className="mt-4 max-w-2xl text-base leading-relaxed text-background-100/90 md:text-lg">
              {t("hero.subtitle")}
            </p>

            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 cursor-pointer transition-colors hover:bg-accent-600 sm:w-auto"
              >
                <span className="flex h-5 w-5 items-center justify-center mr-2">
                  <i className="ri-grid-line text-base"></i>
                </span>
                {t("hero.ctaPrimary")}
              </a>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-900 cursor-pointer transition-colors hover:bg-background-100 sm:w-auto"
              >
                <span className="flex h-5 w-5 items-center justify-center mr-2">
                  <i className="ri-shopping-bag-3-line text-base"></i>
                </span>
                {t("hero.ctaSecondary")}
              </a>
            </div>
          </div>
        </section>

        {/* Dropdown selector */}
        <section className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <div className="flex justify-center">
            <CategoryDropdown />
          </div>
        </section>

        {/* Category buttons */}
        <section id="categories" className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground-900 md:text-3xl">
              {t("categories.title")}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-foreground-600 md:text-base">
              {t("categories.subtitle")}
            </p>
          </div>
          <div className="mt-8">
            <CategoryGrid />
          </div>
        </section>

        {/* Featured Products */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground-900 md:text-3xl">
              {t("products.featuredTitle")}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-foreground-600 md:text-base">
              {t("products.featuredSubtitle")}
            </p>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Link
              to="/products"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 cursor-pointer transition-colors hover:bg-primary-600"
            >
              {t("products.viewAll")}
              <span className="ml-2 flex h-5 w-5 items-center justify-center">
                <i className="ri-arrow-right-line text-base"></i>
              </span>
            </Link>
          </div>
        </section>

        {/* Text module */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6">
          <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
            <div className="overflow-hidden rounded-lg">
              <img
                src="https://readdy.ai/api/search-image?query=Neatly%20organized%20fashion%20and%20streetwear%20clothing%20collection%20with%20sneakers%20jackets%20and%20accessories%20displayed%20on%20minimal%20wooden%20shelves%2C%20clean%20bright%20Scandinavian%20style%20interior%2C%20soft%20warm%20natural%20lighting%2C%20fresh%20airy%20aesthetic%2C%20editorial%20photography&width=800&height=600&seq=allchina-text-2026&orientation=landscape"
                alt="AllChinaBuy spreadsheet category resources"
                title="AllChinaBuy spreadsheet categories"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground-900 md:text-3xl">
                {t("text.title")}
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                {t("text.paragraph1")}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                {t("text.paragraph2")}
              </p>
            </div>
          </div>
        </section>

        {/* Article link */}
        <section className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <div className="flex flex-col items-center gap-6 rounded-lg border border-background-200 bg-background-100 px-6 py-10 text-center md:py-14">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-article-line text-2xl"></i>
            </span>
            <div>
              <h2 className="text-2xl font-bold text-foreground-900">{t("articleLink.title")}</h2>
              <p className="mx-auto mt-3 max-w-xl text-sm text-foreground-600 md:text-base">
                {t("articleLink.subtitle")}
              </p>
            </div>
            <Link
              to="/spreadsheet-guide"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 cursor-pointer transition-colors hover:bg-primary-600"
            >
              {t("articleLink.cta")}
              <span className="ml-2 flex h-5 w-5 items-center justify-center">
                <i className="ri-arrow-right-line text-base"></i>
              </span>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}